import React from 'react';
import SEOHead from '../components/SEOHead';

const Terms = () => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const termsSchema = {
    "@type": "WebPage",
    "@id": "https://americanprimellc.com/terms#webpage",
    "url": "https://americanprimellc.com/terms",
    "name": "Terms & Conditions | American Prime LLC",
    "description": "Terms and Conditions for American Prime LLC AI automation and business consulting services.",
    "publisher": {
      "@id": "https://americanprimellc.com/#organization"
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)', paddingTop: '80px' }}>
      <SEOHead 
        title="Terms & Conditions"
        description="Terms and Conditions for American Prime LLC. Read about our service agreements, payment terms, liability limitations, and legal policies for AI automation and business consulting services."
        canonicalPath="/terms"
        schema={termsSchema}
      />
      <div className="max-w-4xl mx-auto py-24" style={{ padding: '120px 7.6923% 100px' }}>
        <h1 className="display-large mb-4" style={{ color: 'var(--text-primary)' }}>
          Terms & Conditions
        </h1>
        <p className="heading-3 mb-2" style={{ color: 'var(--brand-primary)' }}>
          American Prime LLC
        </p>
        <p className="body-medium mb-8" style={{ color: 'var(--text-muted)' }}>
          Last Updated: {today}
        </p>
        
        <div className="space-y-8">
          {/* Section 1 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              1. Acceptance of Terms
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              By accessing or using the website, services, or products provided by American Prime LLC ("Company," "we," "us," or "our"), you agree to be bound by these Terms & Conditions. If you do not agree, please do not use our services.
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              2. Services
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              American Prime LLC provides AI automation, business consulting, digital transformation, and related professional services. All services are offered on a best-effort basis and may evolve over time.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              We reserve the right to modify, suspend, or discontinue any service at any time without liability.
            </p>
          </div>

          {/* Section 3 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              3. No Guarantees or Warranties
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              While we strive to deliver high-quality services, we do not guarantee specific results, outcomes, revenue, performance improvements, or business success.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              All services are provided "as is" and "as available", without warranties of any kind, either express or implied.
            </p>
          </div>

          {/* Section 4 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              4. Client Responsibilities
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Clients agree to:
            </p>
            <ul className="space-y-2" style={{ paddingLeft: '20px' }}>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Provide accurate and complete information
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Use services lawfully and ethically
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Make timely payments where applicable
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Not misuse, resell, or reverse-engineer any proprietary materials
              </li>
            </ul>
          </div>

          {/* Section 5 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              5. Payments & Billing
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              We accept global payment methods including:
            </p>
            <ul className="space-y-2 mb-4" style={{ paddingLeft: '20px' }}>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Major credit cards (Visa, MasterCard, American Express)
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • ACH bank transfers
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Wire transfers
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • PayPal, Zelle, Venmo
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Other international payment methods as agreed
              </li>
            </ul>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              All payments are non-refundable unless explicitly stated in writing.
            </p>
          </div>

          {/* Section 6 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              6. Contracts & Renewals
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              We offer flexible month-to-month agreements as well as long-term contract options.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Services may be discontinued prior to contract end based on agreed terms. Contracts may renew automatically unless canceled in accordance with contract conditions.
            </p>
          </div>

          {/* Section 7 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              7. Limitation of Liability
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              To the maximum extent permitted by law:
            </p>
            <ul className="space-y-2 mb-4" style={{ paddingLeft: '20px' }}>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • American Prime LLC shall not be liable for indirect, incidental, special, consequential, or punitive damages
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Our total liability shall not exceed the amount paid by the client for services in the preceding 3 months
              </li>
            </ul>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              This applies even if we were advised of potential damages.
            </p>
          </div>

          {/* Section 8 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              8. Indemnification
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              You agree to indemnify and hold harmless American Prime LLC from any claims, losses, damages, or expenses arising from:
            </p>
            <ul className="space-y-2" style={{ paddingLeft: '20px' }}>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Your misuse of services
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Violation of laws
              </li>
              <li className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                • Violation of these Terms
              </li>
            </ul>
          </div>

          {/* Section 9 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              9. Intellectual Property
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              All content, software, designs, processes, and materials remain the exclusive property of American Prime LLC unless otherwise agreed in writing.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Clients receive a limited, non-transferable license to use deliverables for their intended business purposes only.
            </p>
          </div>

          {/* Section 10 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              10. Confidentiality
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Both parties agree to maintain confidentiality of non-public business information shared during the engagement, except where disclosure is required by law.
            </p>
          </div>

          {/* Section 11 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              11. AI & Automation Disclaimer
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              AI-powered tools may generate outputs based on probabilistic models. Outputs should be reviewed before use in business, legal, or financial decisions.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              American Prime LLC is not responsible for decisions made solely based on AI outputs.
            </p>
          </div>

          {/* Section 12 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              12. Third-Party Services
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              We may integrate third-party tools or platforms. American Prime LLC is not responsible for third-party services, outages, or changes.
            </p>
          </div>

          {/* Section 13 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              13. Termination
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Either party may terminate services in accordance with agreed terms. Termination does not waive payment obligations incurred prior to termination.
            </p>
          </div>

          {/* Section 14 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              14. Governing Law
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              These Terms shall be governed by and interpreted under the laws of the United States, without regard to conflict-of-law principles.
            </p>
          </div>

          {/* Section 15 */}
          <div>
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              15. Changes to Terms
            </h2>
            <p className="body-medium mb-3" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              American Prime LLC reserves the right to modify, update, or change these Terms & Conditions at any time.
            </p>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Changes become effective immediately upon posting on the website. Continued use of services constitutes acceptance of the revised terms.
            </p>
          </div>

          {/* Section 16 - Contact */}
          <div className="pt-4">
            <h2 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>
              16. Contact Information
            </h2>
            <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              For questions or concerns, contact us at:
            </p>
            <div 
              className="p-6 rounded-lg"
              style={{ 
                background: 'rgba(0, 255, 209, 0.05)', 
                border: '1px solid rgba(0, 255, 209, 0.2)' 
              }}
            >
              <p className="heading-3 mb-2" style={{ color: 'var(--text-primary)' }}>
                American Prime LLC
              </p>
              <a 
                href="mailto:info@americanprimellc.com"
                className="body-medium block hover:underline"
                style={{ color: 'var(--brand-primary)' }}
              >
                info@americanprimellc.com
              </a>
              <a 
                href="tel:+12035899774"
                className="body-medium block mt-1 hover:underline"
                style={{ color: 'var(--text-secondary)' }}
              >
                +1 (203) 589-9774
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
